import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AuthShell, AuthIcon } from '../../components/layout/AuthShell';
import { Eye, EyeOff, Users, CheckCircle2, AlertCircle, Building2, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AcceptInvitePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setCheckingToken(false);
      setError('Kein gültiger Einladungslink.');
      return;
    }
    axios.get(`${API}/team/invite-info?token=${token}`)
      .then((response) => setInviteData(response.data))
      .catch((err) => setError(err.response?.data?.detail || 'Diese Einladung ist ungültig oder abgelaufen.'))
      .finally(() => setCheckingToken(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    if (password.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/team/accept-invite?token=${token}&password=${encodeURIComponent(password)}`);

      if (response.data.access_token) {
        setSuccess(true);
        localStorage.setItem('token', response.data.access_token);
        setTimeout(() => {
          window.location.href = '/portal';
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      testId="accept-invite-page"
      eyebrow="Team-Einladung"
      title={<>Willkommen <span className="serif-it text-purple">im Team.</span></>}
      intro={inviteData && (<>Du wurdest eingeladen, dem Team von <strong className="text-ink">{inviteData.company_name}</strong> beizutreten.</>)}
    >
      {checkingToken ? (
        <div className="flex justify-center py-6" data-testid="invite-loading">
          <Loader2 className="w-10 h-10 text-purple animate-spin" />
        </div>
      ) : success ? (
        <div className="text-center py-2" data-testid="invite-success">
          <AuthIcon tone="green"><CheckCircle2 className="w-8 h-8" /></AuthIcon>
          <h2 className="text-xl font-bold mb-2">Willkommen im Team!</h2>
          <p className="text-ink/80">
            Dein Konto wurde erfolgreich erstellt. Du wirst zum Portal weitergeleitet...
          </p>
        </div>
      ) : !inviteData ? (
        <div className="text-center py-2" data-testid="invite-invalid">
          <AuthIcon tone="red"><AlertCircle className="w-8 h-8" /></AuthIcon>
          <h2 className="text-xl font-bold mb-2">Ungültige Einladung</h2>
          <p className="text-ink/80 mb-6">{error}</p>
          <Button asChild variant="outline">
            <Link to="/">Zur Startseite</Link>
          </Button>
        </div>
      ) : (
        <>
          <div className="mb-6 p-4 bg-off border-2 border-ink flex items-center gap-3" data-testid="invite-info">
            <div className="w-10 h-10 bg-lilac border-2 border-ink flex items-center justify-center flex-shrink-0">
              <Building2 className="w-5 h-5 text-purple" />
            </div>
            <div>
              <p className="font-display font-bold">{inviteData.company_name}</p>
              <p className="text-sm text-ink/80">Eingeladen von {inviteData.inviter_name}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="mb-2 block">Dein Name</Label>
              <Input value={inviteData.name} disabled />
            </div>

            <div>
              <Label className="mb-2 block">E-Mail</Label>
              <Input value={inviteData.email} disabled />
            </div>

            <div>
              <Label htmlFor="password" className="mb-2 block">Passwort festlegen *</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mindestens 6 Zeichen"
                  required
                  minLength={6}
                  className="pr-12"
                  data-testid="invite-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/60 hover:text-ink"
                  aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="mb-2 block">Passwort bestätigen *</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Passwort wiederholen"
                required
                data-testid="invite-confirm-password"
              />
            </div>

            {error && (
              <p className="text-red-700 font-medium" data-testid="invite-error">{error}</p>
            )}

            <Button type="submit" disabled={loading} className="btn--block" data-testid="invite-submit">
              {loading ? 'Wird erstellt...' : (<><Users className="w-5 h-5" />Dem Team beitreten</>)}
            </Button>
          </form>
        </>
      )}
    </AuthShell>
  );
};

export default AcceptInvitePage;
